/**
 * Builds the received file without keeping every chunk on the JavaScript heap.
 * Chunks are folded into Blobs at a fixed threshold; browsers store Blob data outside the heap and
 * page large Blobs to disk, which is what lets multi-gigabyte transfers finish on ordinary devices.
 */
export type Assembly = Readonly<{
  folded: readonly Blob[];
  pending: readonly ArrayBuffer[];
  pendingBytes: number;
}>;

export const emptyAssembly = (): Assembly => ({ folded: [], pending: [], pendingBytes: 0 });

export const appendChunk = (assembly: Assembly, chunk: ArrayBuffer, foldThresholdBytes: number): Assembly => {
  const pending = [...assembly.pending, chunk];
  const pendingBytes = assembly.pendingBytes + chunk.byteLength;
  if (pendingBytes < foldThresholdBytes) {
    return { folded: assembly.folded, pending, pendingBytes };
  }
  return { folded: [...assembly.folded, new Blob(pending)], pending: [], pendingBytes: 0 };
};

export const finishAssembly = (assembly: Assembly, mimeType: string): Blob =>
  new Blob([...assembly.folded, ...assembly.pending], { type: mimeType });

export const assembledBytes = (assembly: Assembly): number =>
  assembly.folded.reduce((total, blob) => total + blob.size, 0) + assembly.pendingBytes;
